import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, AutoComplete, Button, Form, Select, Spin, Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setProviders } from '../../../contextRedux/Provider/ProviderStore';
import { setProviderWorkPlace, updateField } from '../../../contextRedux/ProviderWorkPlace/ProviderWorkPlaceModel';
import ProviderService from '../../../services/provider/ProviderService';
import ProviderWorkPlaceService from '../../../services/providerWorkPlace/ProviderWorkPlaceService';
import { RootState } from '../../../store';
import { Tab } from '../../../utils/tabs';
import LocationTab from './Tabs/LocationTab/LocationTab';

const NewProviderWorkPlace: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleResponse = useHandleResponse();

  const { Option } = Select;

  const location = useLocation();

  const useQueryParams = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQueryParams();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [id, setId] = useState<string | null>(query.get('id'));
  const [searchTerm, setSearchTerm] = useState<string>('');

  const reduxProviderWorkPlace = useSelector((state: RootState) => state?.models?.providerWorkPlace);
  const initialValues = reduxProviderWorkPlace || {};

  const { data: _providerWorkPlace, isFetching: isProviderWorkPlaceLoading } = useQuery({
    queryKey: ['providerWorkPlaces'],
    queryFn: () =>
      ProviderWorkPlaceService.search({
        ids: [id!],
        isProvider: true,
      }).then((data) => {
        dispatch(setProviderWorkPlace(data?.response[0]));
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: providers, isFetching: isProvidersLoading } = useQuery({
    queryKey: ['providers'],
    queryFn: () =>
      ProviderService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setProviders(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });
  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().includes(inputValue.toLowerCase());
  };

  const handleSelectProvider = (value: string) => {
    const provider = providers?.find((provider: any) => provider?.name === value);
    if (provider) {
      const fieldsValue = {
        ...form?.getFieldsValue(true),
        providerId: provider?.id,
        provider: { name: provider?.name, id: provider?.id },
      };
      form.setFieldsValue(fieldsValue);
      dispatch(updateField({ field: 'providerId', value: provider?.id }));
      dispatch(updateField({ field: 'provider', value: provider }));
    }
  };

  const handleFieldChange = (changedValues: any, _allValues: any) => {
    for (const key in changedValues) {
      dispatch(updateField({ field: key, value: changedValues[key] }));
    }
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveProviderWorkPlace = async () => {
    try {
      await onSaveProviderWorkPlace();
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const onSaveProviderWorkPlace = async () => {
    saveProviderWorkPlace(reduxProviderWorkPlace);
  };

  const { mutate: saveProviderWorkPlace, isPending: _isSavingUser } = useMutation({
    mutationFn: (formData: any) => ProviderWorkPlaceService.upsert(id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        data?.response && navigate(`/administration/providerWorkPlace/edit?id=${data?.response?.id}`);
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl);
  }, [location.search]);

  useEffect(() => {
    if (id) {
      dispatch(updateField({ field: 'id', value: id }));
    } else {
      dispatch(updateField({ field: 'id', value: null }));
    }
  }, [id, dispatch]);

  const tabs: Tab[] = [
    {
      key: 'locationTab',
      label: 'Locatia',
      children: <LocationTab />,
    },
  ];

  if (isProviderWorkPlaceLoading || isProvidersLoading) {
    return <Spin size='large' className='spinner'></Spin>;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <h2>{id ? 'Punct De Depozitare - ' + reduxProviderWorkPlace?.name : 'Punct De Depozitare'}</h2>
        <div>
          <Button onClick={() => handleSaveProviderWorkPlace()}>Salveaza</Button>
        </div>
      </div>
      <Form
        form={form}
        name='workPlaceForm'
        initialValues={initialValues}
        onValuesChange={handleFieldChange}
        layout='vertical'
      >
        <Form.Item name={['provider', 'name']} rules={[{ required: true, message: 'Introdu provider!' }]}>
          <AutoComplete
            onSelect={handleSelectProvider}
            onChange={(value: string) => handleSearchTermChange(value)}
            filterOption={handleFilter}
            value={searchTerm}
            style={{ width: '100%' }}
          >
            {providers?.map((provider: any, index: any) => (
              <Option key={index} value={provider?.name}>
                {provider?.name}
              </Option>
            ))}
          </AutoComplete>
        </Form.Item>
      </Form>
      <Tabs defaultActiveKey='1' items={tabs} />
    </>
  );
};

export default NewProviderWorkPlace;
